'use client'

import LinkSearchForm from '@/components/forms/link-search-form'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import useDisclosure from '@/hooks/use-disclosure'
import { Spinner } from './spinner'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { getLinkSearch } from '@/apis/fetchers/search/get-link-search'
import { AppRoutes } from '@/routes'
import { useMutation } from '@tanstack/react-query'
import { gaSearch } from '@/utils/gtag'
import { useFullDomainUrl } from '@/hooks/use-full-domain-url'
import { useCurrentUser } from '@/hooks/use-current-user'

const URL_REGEX =
  /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)$/

interface LinkSearchFormDrawerProps {
  trigger: React.ReactNode
}

const POLLING_INTERVAL_MS = 3000
const MAX_REQUEST_COUNT = 5

export const formSchema = z.object({
  url: z.string(),
})

function LinkSearchFormDrawer({ trigger }: LinkSearchFormDrawerProps) {
  const { isOpen, setIsOpen } = useDisclosure()
  const { user } = useCurrentUser()
  const fullDomainUrl = useFullDomainUrl()

  const router = useRouter()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: '',
    },
  })
  const [isLinkSearching, setIsLinkSearching] = useState(false)
  const pollLinkSearch = (url: string, attempts = 0) => {
    setIsLinkSearching(true)

    if (attempts >= MAX_REQUEST_COUNT) {
      form.setError('url', {
        message: 'url에 해당하는 상품을 찾을 수 없습니다.',
      })
      setIsLinkSearching(false)
      return Promise.resolve()
    }

    return getLinkSearch({ url })
      .then((data) => {
        if (data.statusCode === 202) {
          setTimeout(
            () => pollLinkSearch(url, attempts + 1),
            POLLING_INTERVAL_MS,
          )
        } else if (data.statusCode === 200) {
          const productId = data.data.results[0].productId
          router.push(AppRoutes.product(productId))
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const { mutate } = useMutation({
    mutationKey: ['link-search'],
    mutationFn: (url: string) => pollLinkSearch(url),
  })

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (values.url.length <= 0) {
      form.setError('url', { message: '상품 링크를 입력해주세요.' })
      return
    }
    const splitValue = values.url.split('https://')

    const url =
      splitValue.length > 1
        ? `https://${splitValue[splitValue.length - 1]}`
        : splitValue[0]

    if (!url.match(URL_REGEX)) {
      form.setError('url', { message: '올바른 url을 입력했는지 확인해주세요.' })
      return
    }

    gaSearch({
      pageUrl: fullDomainUrl,
      searchTerm: url,
      searchResultsCount: 0,
      searchType: 'link',
      userId: user?.id || null,
    })

    mutate(url)
  }

  const errorMessage = form.formState.errors.url?.message

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(value) => {
        if (!value) {
          form.clearErrors()
          form.setValue('url', '')
        }
        setIsOpen(value)
      }}
    >
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent className="screen mx-auto px-5 pb-6">
        <DrawerHeader className="py-5 *:text-center">
          {isLinkSearching ? (
            <div className="flex flex-col items-center gap-[12px]">
              <Spinner size={50} />
              <div className="text-caption-01-medium text-gray-5">
                쿠팡에서 상품 정보를 가져오고 있어요 (최대 10초 소요)
              </div>
            </div>
          ) : (
            <>
              <DrawerTitle className="!text-heading-02-bold text-black">
                쿠팡 상품링크
              </DrawerTitle>
              <DrawerDescription className="!text-caption-01-medium text-gray-5">
                찾으시는 상품의 링크를 직접 입력해 검색해보세요!
              </DrawerDescription>
            </>
          )}
        </DrawerHeader>

        <LinkSearchForm
          form={form}
          handleSubmit={form.handleSubmit(onSubmit)}
          errorMessage={errorMessage}
          disabled={isLinkSearching}
        />

        <DrawerFooter className="mt-5 py-0">
          <ul className="list-disc text-caption-01-medium text-gray-5">
            <li>성인 인증이 필요한 상품은 추가할 수 없어요.</li>
            <li>정확한 URL인지 확인해 주세요.</li>
          </ul>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}

export default LinkSearchFormDrawer
